import React, {useEffect, useMemo, useState} from 'react';
import {Modal, NumberInput, Select, Textarea, TextInput} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Divider} from 'src/design/atoms/Divider';
import {Grid} from 'src/design/atoms/Grid';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useFieldArray, useForm} from 'react-hook-form';
import {z} from 'zod';
import type {OnDragEndResponder} from '@hello-pangea/dnd';
import type {
  SkillCriterionRequest,
  SkillResponse,
} from 'src/api/ProactApiClient';
import {SkillCriterionEvidenceTypeEnum} from 'src/api/ProactApiClient';
import {Button} from 'src/design/atoms/Button';
import {DraggableList} from 'src/design/molecules/DraggableList';
import {useCreateSkillMutation, useUpdateSkillMutation} from 'src/redux/queries/skill';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';

export interface SkillFormModalProps {
  opened: boolean;
  onClose: () => void;
  skill?: SkillResponse;
}

// Photo is not offered in the picker; the label map still covers it so
// existing criteria authored with it render correctly.
const evidenceTypeLabel: Record<SkillCriterionEvidenceTypeEnum, string> = {
  [SkillCriterionEvidenceTypeEnum.Invalid]: 'Invalid',
  [SkillCriterionEvidenceTypeEnum.None]: 'None',
  [SkillCriterionEvidenceTypeEnum.Photo]: 'Photo (legacy)',
  [SkillCriterionEvidenceTypeEnum.Note]: 'Note',
};

const evidenceTypeOptions = [
  {value: String(SkillCriterionEvidenceTypeEnum.None), label: 'None'},
  {value: String(SkillCriterionEvidenceTypeEnum.Note), label: 'Note'},
];

// Field limits mirror the backend entity constraints (SkillsModelBuilder).
const skillSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'Name is required')
    .max(255, 'Name must be 255 characters or fewer'),
  description: z
    .string()
    .trim()
    .max(2000, 'Description must be 2000 characters or fewer'),
  active: z.boolean(),
  expiryPeriodDays: z.union([
    z.literal(''),
    z
      .number()
      .int('Expiry must be a whole number of days')
      .min(1, 'Expiry must be at least 1 day'),
  ]),
  criteria: z
    .array(
      z.object({
        criterionId: z.number().optional(),
        text: z
          .string()
          .trim()
          .min(1, 'Criterion text is required')
          .max(1000, 'Criterion text must be 1000 characters or fewer'),
        evidenceType: z.nativeEnum(SkillCriterionEvidenceTypeEnum),
      })
    )
    .min(1, 'Add at least one criterion'),
});

type SkillFormValues = z.infer<typeof skillSchema>;

const toFormValues = (skill?: SkillResponse): SkillFormValues => ({
  name: skill?.name ?? '',
  description: skill?.description ?? '',
  active: skill?.active ?? true,
  expiryPeriodDays: skill?.expiryPeriodDays ?? '',
  criteria: (skill?.criteria ?? [])
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(criterion => ({
      criterionId: criterion.id,
      text: criterion.text,
      evidenceType: criterion.evidenceType,
    })),
});

/**
 * Create/edit modal for a Skill. Criteria use the same outline-then-editor
 * split as the course Build tab: select an item, edit it in the side panel.
 */
export const SkillFormModal: React.FC<SkillFormModalProps> = ({
  opened,
  onClose,
  skill,
}) => {
  const {setAlert} = useAlert();
  const isEdit = skill !== undefined;

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [createSkill, {isLoading: isCreating}] = useCreateSkillMutation();
  const [updateSkill, {isLoading: isUpdating}] = useUpdateSkillMutation();
  const isSaving = isCreating || isUpdating;

  const initialValues = useMemo(() => toFormValues(skill), [skill]);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: {isValid},
  } = useForm<SkillFormValues>({
    defaultValues: initialValues,
    resolver: zodResolver(skillSchema),
    mode: 'onChange',
  });

  const {fields, append, remove, move} = useFieldArray({
    control,
    name: 'criteria',
  });

  useEffect(() => {
    if (!opened) {
      return;
    }
    reset(initialValues);
    setSelectedIndex(0);
  }, [opened, initialValues, reset]);

  const criteriaValues = watch('criteria');

  const indexOfKey = (key: string) =>
    fields.findIndex(field => field.id === key);

  // Keep the selection on the same item when it moves within the list.
  const moveAndFollow = (from: number, to: number) => {
    if (from === -1 || to < 0 || to >= fields.length) {
      return;
    }
    move(from, to);
    setSelectedIndex(current => {
      if (current === from) {
        return to;
      }
      if (from < current && to >= current) {
        return current - 1;
      }
      if (from > current && to <= current) {
        return current + 1;
      }
      return current;
    });
  };

  const handleAddCriterion = () => {
    append({text: '', evidenceType: SkillCriterionEvidenceTypeEnum.None});
    setSelectedIndex(fields.length);
  };

  const handleDeleteCriterion = (key: string) => {
    const index = indexOfKey(key);
    if (index === -1) {
      return;
    }
    remove(index);
    setSelectedIndex(current => {
      if (current === index) {
        return 0;
      }
      return current > index ? current - 1 : current;
    });
  };

  const handleDragEnd: OnDragEndResponder = result => {
    if (!result.destination) {
      return;
    }
    moveAndFollow(result.source.index, result.destination.index);
  };

  const selectedCriterion = criteriaValues[selectedIndex];

  const onSubmit = async (values: SkillFormValues) => {
    const criteriaRequest: SkillCriterionRequest[] = values.criteria.map(
      (item, index) => ({
        id: item.criterionId,
        order: index,
        text: item.text,
        evidenceType: item.evidenceType,
      })
    );

    const request = {
      name: values.name,
      description: values.description || undefined,
      active: values.active,
      expiryPeriodDays:
        values.expiryPeriodDays === '' ? undefined : values.expiryPeriodDays,
      criteria: criteriaRequest,
    };

    try {
      if (isEdit) {
        await updateSkill({...request, id: skill.id}).unwrap();
        setAlert(`${values.name} was successfully updated.`, 'success');
      } else {
        await createSkill(request).unwrap();
        setAlert(`${values.name} was successfully created.`, 'success');
      }
      onClose();
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={isEdit ? 'Edit Skill' : 'New Skill'}
      centered
      size="xl">
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap="md">
          <Controller
            name="name"
            control={control}
            render={({field, fieldState}) => (
              <TextInput
                label="Name"
                placeholder="e.g. Forklift Operation"
                required
                maxLength={255}
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({field, fieldState}) => (
              <Textarea
                label="Description"
                placeholder="What this skill covers"
                maxLength={2000}
                {...field}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="expiryPeriodDays"
            control={control}
            render={({field, fieldState}) => (
              <NumberInput
                label="Expires after (days)"
                description="Leave blank if this sign-off never expires"
                placeholder="e.g. 365"
                min={1}
                allowDecimal={false}
                allowNegative={false}
                value={field.value}
                onChange={value => field.onChange(value === '' ? '' : Number(value))}
                onBlur={field.onBlur}
                error={fieldState.error?.message}
                w={240}
              />
            )}
          />
          <Controller
            name="active"
            control={control}
            render={({field}) => (
              <Switch
                label="Active"
                description="Inactive skills cannot be chosen for new sign-off requests"
                checked={field.value}
                onChange={event => field.onChange(event.currentTarget.checked)}
              />
            )}
          />

          <Divider />

          <Box>
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={500}>
                Criteria
              </Text>
              <Button size="xs" variant="subtle" onClick={handleAddCriterion}>
                Add criterion
              </Button>
            </Group>

            {fields.length === 0 ? (
              <Alert color="yellow" py="xs">
                Add at least one criterion for the evaluator to assess.
              </Alert>
            ) : (
              <Grid>
                <Grid.Col span={5}>
                  <DraggableList
                    items={fields.map((field, index) => ({
                      id: field.id,
                      primary:
                        criteriaValues[index]?.text || 'Untitled criterion',
                      secondary:
                        evidenceTypeLabel[
                          criteriaValues[index]?.evidenceType ??
                            SkillCriterionEvidenceTypeEnum.None
                        ],
                    }))}
                    selectedIndex={selectedIndex}
                    onDragEnd={handleDragEnd}
                    onSelect={key => setSelectedIndex(indexOfKey(key))}
                    onDelete={handleDeleteCriterion}
                    onMoveUp={key => {
                      const index = indexOfKey(key);
                      moveAndFollow(index, index - 1);
                    }}
                    onMoveDown={key => {
                      const index = indexOfKey(key);
                      moveAndFollow(index, index + 1);
                    }}
                  />
                </Grid.Col>
                <Grid.Col span={7}>
                  {selectedCriterion ? (
                    <Stack gap="sm">
                      <Controller
                        key={fields[selectedIndex]?.id}
                        name={`criteria.${selectedIndex}.text`}
                        control={control}
                        render={({field, fieldState}) => (
                          <Textarea
                            label="Criterion text"
                            placeholder="e.g. Performs pre-operation inspection"
                            required
                            maxLength={1000}
                            {...field}
                            error={fieldState.error?.message}
                          />
                        )}
                      />
                      <Controller
                        name={`criteria.${selectedIndex}.evidenceType`}
                        control={control}
                        render={({field}) => (
                          <Select
                            label="Evidence type"
                            description={
                              field.value ===
                              SkillCriterionEvidenceTypeEnum.Photo
                                ? 'Photo evidence was set before upload support existed. Switch this to Note to capture evidence today.'
                                : undefined
                            }
                            data={
                              field.value ===
                              SkillCriterionEvidenceTypeEnum.Photo
                                ? [
                                    ...evidenceTypeOptions,
                                    {
                                      value: String(
                                        SkillCriterionEvidenceTypeEnum.Photo
                                      ),
                                      label:
                                        evidenceTypeLabel[
                                          SkillCriterionEvidenceTypeEnum.Photo
                                        ],
                                    },
                                  ]
                                : evidenceTypeOptions
                            }
                            value={String(field.value)}
                            comboboxProps={{withinPortal: true, zIndex: 1001}}
                            onChange={value =>
                              field.onChange(
                                Number(value) as SkillCriterionEvidenceTypeEnum
                              )
                            }
                          />
                        )}
                      />
                    </Stack>
                  ) : (
                    <Text c="dimmed" size="sm">
                      Select a criterion to edit it.
                    </Text>
                  )}
                </Grid.Col>
              </Grid>
            )}
          </Box>

          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isValid || isSaving}
              loading={isSaving}>
              {isEdit ? 'Save Changes' : 'Create Skill'}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
